import { KeyboardEvent, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faComment } from '@fortawesome/pro-solid-svg-icons/faComment';
import { faEyeSlash } from '@fortawesome/pro-solid-svg-icons/faEyeSlash';
import { faFlag } from '@fortawesome/pro-solid-svg-icons/faFlag';
import { faHeart } from '@fortawesome/pro-solid-svg-icons/faHeart';
import { faHeart as faHeartRegular } from '@fortawesome/pro-regular-svg-icons/faHeart';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar';
import { faUserGroup } from '@fortawesome/pro-solid-svg-icons/faUserGroup';
import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { FlagProfileModal } from 'us-react-components/dist/FlagProfileModal/FlagProfileModal';
import {
  SitterNoteModal,
  SitterNoteModalNote,
} from 'us-react-components/dist/SitterNoteModal/SitterNoteModal';
import { Sitter } from 'us-web-services';

import { Button, ButtonType, RatingStars } from '../../../components';

type Props = {
  sitter: Sitter;
  parentId: number;
  parentKidsCount: number;
  setFavorite: (isFavorited: boolean, favoriteId: number, sitterId: number) => Promise<void>;
  onToggleHidden: (sitter: Sitter) => Promise<void>;
  onBook: (sitter: Sitter) => void;
  onOpenProfile: (sitter: Sitter) => void;
  onNoteSaved: (sitterId: number, note?: SitterNoteModalNote) => void;
};

const NOTE_LIMIT = 300;
const ABOUT_LIMIT = 200;

const formatNoteDate = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const getRateForKidCount = (sitter: Sitter, kidCount: number): number | undefined => {
  const rates = sitter.rates ?? [];
  if (rates.length === 0) return undefined;
  const idx = Math.min(Math.max(kidCount, 1), rates.length) - 1;
  return rates[idx]?.minimumRate;
};

// connections on the wire include affiliation/school/localGroup/indirect lists
// that the typed Relationship doesn't expose; reach in via unknown casts.
interface ConnectionsShape {
  indirect?: unknown[];
  affiliation?: unknown[];
  school?: unknown[];
  localGroup?: unknown[];
}

const getConnections = (sitter: Sitter): ConnectionsShape => {
  const raw = (sitter.relationship?.connections ?? {}) as ConnectionsShape;
  return raw;
};

const arrLen = (x?: unknown): number => (Array.isArray(x) ? x.length : 0);

export const YourSittersCard = ({
  sitter,
  parentId,
  parentKidsCount,
  setFavorite,
  onToggleHidden,
  onBook,
  onOpenProfile,
  onNoteSaved,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const [hideBusy, setHideBusy] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [noteExpanded, setNoteExpanded] = useState(false);

  const isFavorited = !!sitter.relationship?.bookmarked;
  const favoriteId = sitter.relationship?.favorite?.id ?? 0;
  const blockedId = (sitter.relationship as { blocked?: unknown } | undefined)?.blocked;
  const isBlocked = typeof blockedId === 'number' && blockedId > 0;
  const bookingCount = sitter.relationship?.bookingCount ?? 0;
  const distance = sitter.relationship?.distance;
  const city = sitter.address?.city;
  const neighborhood = sitter.address?.neighborhood;
  const lastAccessLabel = sitter.userStats?.lastAccessLabel;

  const parentNote = sitter.relationship?.parentNote;
  const repeatFamilies = sitter.repeatFamilies ?? 0;
  const responseTime = sitter.responseTime ?? 0;
  const kidCount = Math.max(parentKidsCount, 1);
  const rate = getRateForKidCount(sitter, kidCount);
  const lastMessaged = sitter.relationship?.lastMessaged;
  const positiveReview = !!sitter.relationship?.positiveReview;

  // Mirrors Angular sitter-card-summary.showAboutInfo(): true when there are
  // no prior interaction signals. Gates the about-snippet block below; the
  // indirect / common-group connection counts render in the opposite branch
  // (see `!showAboutInfo` further down).
  const showAboutInfo = !positiveReview && bookingCount === 0 && !parentNote;
  const connections = getConnections(sitter);
  const indirectCount = arrLen(connections.indirect);
  const commonGroupCount =
    arrLen(connections.affiliation) + arrLen(connections.school) + arrLen(connections.localGroup);

  const collegeName = sitter.college?.college?.name;
  const collegeAlias = sitter.college?.college?.urlAlias;
  const aboutText = sitter.about;

  const handleCardClick = (): void => onOpenProfile(sitter);

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    // Only activate when the card itself has focus, not a nested link/button —
    // keyboard activation of inner controls bubbles here and would otherwise
    // also open the profile.
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpenProfile(sitter);
    }
  };

  const handleBookClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    onBook(sitter);
  };

  const handleFavoriteClick = async (e: MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.stopPropagation();
    if (!sitter.id || favoriteBusy) return;
    setFavoriteBusy(true);
    try {
      await setFavorite(!isFavorited, favoriteId, sitter.id);
    } finally {
      setFavoriteBusy(false);
    }
  };

  const handleHideClick = async (e: MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.stopPropagation();
    if (hideBusy) return;
    setHideBusy(true);
    try {
      await onToggleHidden(sitter);
    } finally {
      setHideBusy(false);
    }
  };

  const stop = (e: MouseEvent<HTMLElement>): void => e.stopPropagation();

  const noteComment = parentNote?.comment ?? '';
  const noteIsTruncated = noteComment.length > NOTE_LIMIT;
  const displayedNote =
    noteIsTruncated && !noteExpanded ? `${noteComment.slice(0, NOTE_LIMIT)}…` : noteComment;
  const aboutIsTruncated = (aboutText?.length ?? 0) > ABOUT_LIMIT;
  const displayedAbout = aboutText
    ? aboutIsTruncated
      ? `${aboutText.slice(0, ABOUT_LIMIT)}…`
      : aboutText
    : '';

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      className='block w-full text-left bg-white border-2 border-sapphire-50 rounded-lg p-4 mb-4 hover:border-sapphire-100 transition-colors cursor-pointer'
    >
      <div className='flex items-start gap-4'>
        <div className='relative flex-shrink-0'>
          <img
            src={sitter.pictureUrl}
            alt={sitter.firstName ?? ''}
            className={`w-20 h-20 rounded-full object-cover ${isBlocked ? 'opacity-40' : ''}`}
          />
          {isBlocked && (
            <span className='absolute inset-0 flex items-center justify-center'>
              <FontAwesomeIcon icon={faEyeSlash} className='text-2xl text-neutral-600' />
            </span>
          )}
          {!isBlocked && (
            <button
              type='button'
              onClick={handleFavoriteClick}
              aria-label={
                isFavorited
                  ? t('pages.yourSitters.card.unfavorite')
                  : t('pages.yourSitters.card.favorite')
              }
              className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white border border-neutral-200 shadow-sm flex items-center justify-center ${
                isFavorited ? 'text-bubblegum' : 'text-neutral-400 hover:text-bubblegum'
              }`}
            >
              <FontAwesomeIcon icon={isFavorited ? faHeart : faHeartRegular} />
            </button>
          )}
        </div>

        <div className='flex-1 min-w-0'>
          <div className='flex items-start justify-between gap-2 flex-wrap'>
            <div className='min-w-0'>
              <h3 className='text-lg font-semibold truncate' id={`sitter-name-link-${sitter.id}`}>
                {sitter.firstName}
              </h3>
              {!!sitter.reviewCount && (
                <div className='mt-0.5 flex items-center gap-2 text-sm text-neutral-700'>
                  <a
                    href={`/app/sitter/${sitter.id}/reviews`}
                    onClick={stop}
                    aria-label={`Reviews for ${sitter.firstName ?? ''}`}
                  >
                    <RatingStars rating={sitter.rating} count={sitter.reviewCount} />
                  </a>
                  <a
                    href={`/app/sitter/${sitter.id}/reviews`}
                    onClick={stop}
                    className='underline text-neutral-700'
                  >
                    {t('pages.yourSitters.card.reviewsCount', { count: sitter.reviewCount })}
                  </a>
                </div>
              )}
            </div>

            {!isBlocked && (
              <Button
                buttonType={ButtonType.primary}
                id={`add-booking-${sitter.id}`}
                className='hidden md:inline-flex'
                onClick={handleBookClick}
              >
                {t('pages.yourSitters.card.requestBooking')}
              </Button>
            )}
          </div>

          {isBlocked && (
            <div className='text-sm font-semibold text-neutral-700 mt-1'>
              {t('pages.yourSitters.card.youHidden')}
            </div>
          )}

          {(city || distance) && (
            <div className='text-sm text-neutral-700 mt-1'>
              {city}
              {neighborhood && <span> - {neighborhood}</span>}
              {distance && (
                <span className='ml-1'>
                  {t('pages.yourSitters.card.distanceMi', { miles: distance })}
                </span>
              )}
            </div>
          )}

          {rate && (
            <div className='text-sm text-neutral-700 mt-1' id={`rate-${sitter.id}`}>
              {t('pages.yourSitters.card.ratePerHour', { rate, count: kidCount })}
            </div>
          )}

          {collegeName && (
            <div className='hidden md:block text-sm text-neutral-700 mt-1'>
              {t('pages.yourSitters.card.collegePrefix')}{' '}
              {collegeAlias ? (
                <a
                  href={`/college-babysitters/${collegeAlias}`}
                  onClick={stop}
                  className='text-sapphire underline'
                >
                  {collegeName}
                </a>
              ) : (
                <span>{collegeName}</span>
              )}
            </div>
          )}

          {bookingCount > 0 && (
            <a
              href={`/app/bookings?filterType=all_date&sitterId=${sitter.id}&page=1`}
              onClick={stop}
              id={`num-bookings-to-list-${sitter.id}`}
              className='block text-sm text-neutral-700 mt-1'
            >
              <FontAwesomeIcon icon={faCheckCircle} className='text-sapphire mr-1' />
              {t('pages.yourSitters.card.bookingsWithYou', { count: bookingCount })}
            </a>
          )}

          {lastMessaged && (
            <a
              href={`/app/parent/${parentId}/messages/${sitter.id}`}
              onClick={stop}
              id={`num-messages-to-messages-${sitter.id}`}
              className='block text-sm text-neutral-700 mt-1'
            >
              <FontAwesomeIcon icon={faComment} className='text-tertiary mr-1' />
              {t('pages.yourSitters.card.lastMessage', { date: formatNoteDate(lastMessaged) })}
            </a>
          )}

          {positiveReview && (
            <a
              href={`/app/sitter/${sitter.id}/reviews`}
              onClick={stop}
              id={`positive-review-to-reviews-${sitter.id}`}
              className='block text-sm text-neutral-700 mt-1'
            >
              <FontAwesomeIcon icon={faStar} className='text-amber-500 mr-1' />
              {t('pages.yourSitters.card.positiveReview')}
            </a>
          )}

          {!showAboutInfo && indirectCount > 0 && (
            <div
              className='text-sm text-neutral-700 mt-1'
              id={`indirect-connections-${sitter.id}`}
            >
              <FontAwesomeIcon icon={faUsers} className='text-tertiary mr-1' />
              {t('pages.yourSitters.card.connectionsKnow', { count: indirectCount })}
            </div>
          )}

          {!showAboutInfo && indirectCount === 0 && commonGroupCount > 0 && (
            <div
              className='text-sm text-neutral-700 mt-1'
              id={`group-connections-${sitter.id}`}
            >
              <FontAwesomeIcon icon={faUsers} className='text-tertiary mr-1' />
              {t('pages.yourSitters.card.commonGroups', { count: commonGroupCount })}
            </div>
          )}

          {repeatFamilies > 0 && (
            <div
              className='text-sm text-neutral-700 mt-1'
              id={`repeat-families-${sitter.id}`}
            >
              <FontAwesomeIcon icon={faUserGroup} className='text-tertiary mr-1' />
              {repeatFamilies > 20
                ? t('pages.yourSitters.card.repeatFamiliesCapped')
                : t('pages.yourSitters.card.repeatFamilies', { count: repeatFamilies })}
            </div>
          )}

          {responseTime > 0 && (
            <div className='text-sm text-neutral-700 mt-1'>
              {t('pages.yourSitters.card.responseTime', { count: responseTime })}
            </div>
          )}

          {lastAccessLabel && (
            <div className='text-sm text-neutral-700 mt-1 lowercase'>
              <FontAwesomeIcon icon={faBolt} className='text-amber-500 mr-1' />
              {t('pages.yourSitters.card.activeAgo', { label: lastAccessLabel })}
            </div>
          )}

          {showAboutInfo && aboutText && (
            <div className='hidden md:block text-sm text-neutral-700 mt-2'>
              &ldquo;{displayedAbout}&rdquo;
              {aboutIsTruncated && (
                <a
                  href={`/app/sitter/${sitter.id}`}
                  onClick={stop}
                  className='ml-1 text-sapphire underline italic'
                >
                  {t('pages.yourSitters.card.aboutMore')}
                </a>
              )}
            </div>
          )}

          {parentNote?.comment && (
            <div className='mt-3 text-sm text-neutral-700 italic'>
              <span>{displayedNote}</span>
              {noteIsTruncated && (
                <Button
                  buttonType={ButtonType.tertiary}
                  onClick={(event): void => {
                    event.stopPropagation();
                    setNoteExpanded((previous) => !previous);
                  }}
                  className='!ml-1 !p-0 !min-w-0 !text-sapphire underline'
                >
                  {noteExpanded
                    ? t('pages.yourSitters.card.viewLess')
                    : t('pages.yourSitters.card.viewMore')}
                </Button>
              )}
              {parentNote.updated && (
                <div className='not-italic text-xs text-neutral-500 mt-1'>
                  <FontAwesomeIcon icon={faLock} className='mr-1' />
                  {formatNoteDate(parentNote.updated)}
                </div>
              )}
            </div>
          )}

          <div className='mt-3 flex flex-wrap items-center gap-3'>
            <Button
              buttonType={ButtonType.tertiary}
              id={`create-or-edit-note-${sitter.id}`}
              onClick={(e): void => {
                e.stopPropagation();
                setNoteOpen(true);
              }}
              className='!p-0 !min-w-0 !text-sapphire underline text-sm'
            >
              {parentNote?.comment
                ? t('pages.yourSitters.card.editNote')
                : t('pages.yourSitters.card.savePrivateNote')}
            </Button>
          </div>

          <div className='mt-3 flex flex-wrap gap-2 md:hidden'>
            {!isBlocked && (
              <Button
                buttonType={ButtonType.primary}
                id={`add-booking-mobile-${sitter.id}`}
                onClick={handleBookClick}
              >
                {t('pages.yourSitters.card.requestBooking')}
              </Button>
            )}
            <Button
              buttonType={ButtonType.secondary}
              id={`toggle-hidden-mobile-${sitter.id}`}
              onClick={handleHideClick}
            >
              {isBlocked
                ? t('pages.yourSitters.card.unhideCaregiver')
                : t('pages.yourSitters.card.hideCaregiver')}
            </Button>
            <Button
              buttonType={ButtonType.tertiary}
              id={`report-sitter-mobile-${sitter.id}`}
              onClick={(e): void => {
                e.stopPropagation();
                setReportOpen(true);
              }}
            >
              <FontAwesomeIcon icon={faFlag} className='mr-1' />
              {t('pages.yourSitters.card.reportCaregiver')}
            </Button>
          </div>

          <div className='mt-3 hidden md:flex justify-end items-center gap-2'>
            <Button
              buttonType={ButtonType.tertiary}
              id={`report-sitter-${sitter.id}`}
              onClick={(e): void => {
                e.stopPropagation();
                setReportOpen(true);
              }}
              className='!text-sm'
            >
              <FontAwesomeIcon icon={faFlag} className='mr-1' />
              {t('pages.yourSitters.card.reportCaregiver')}
            </Button>
            <Button
              buttonType={ButtonType.secondary}
              id={`toggle-hidden-${sitter.id}`}
              onClick={handleHideClick}
            >
              {isBlocked
                ? t('pages.yourSitters.card.unhideCaregiver')
                : t('pages.yourSitters.card.hideCaregiver')}
            </Button>
          </div>
        </div>
      </div>

      {sitter.id !== undefined && noteOpen && (
        <SitterNoteModal
          isOpen={noteOpen}
          onClose={(): void => setNoteOpen(false)}
          sitterId={sitter.id}
          parentId={parentId}
          initialNote={parentNote}
          onSaved={(note): void => onNoteSaved(sitter.id!, note)}
        />
      )}
      {sitter.id !== undefined && reportOpen && (
        <FlagProfileModal
          isOpen={reportOpen}
          onClose={(): void => setReportOpen(false)}
          flaggedId={sitter.id}
          flaggingId={parentId}
        />
      )}
    </div>
  );
};
